import type { CSSProperties } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  itemLabel: string
}

function pageItems(page: number, totalPages: number): Array<number | 'ellipsis'> {
  const pages = new Set<number>([1, totalPages, page - 1, page, page + 1])
  const visible = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b)

  const items: Array<number | 'ellipsis'> = []
  for (const current of visible) {
    const previous = items[items.length - 1]
    if (typeof previous === 'number' && current - previous > 1) {
      items.push('ellipsis')
    }
    items.push(current)
  }
  return items
}

export default function Pagination({ page, pageSize, total, onPageChange, itemLabel }: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(page, 1), totalPages)
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1
  const end = Math.min(total, safePage * pageSize)

  return (
    <div style={wrapperStyle}>
      <span style={summaryStyle}>
        Total {total.toLocaleString()} {itemLabel}
        {total > 0 ? `, showing ${start.toLocaleString()}-${end.toLocaleString()}` : ''}
      </span>

      <nav style={controlsStyle} aria-label="Pagination">
        <button
          type="button"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          aria-label="Previous page"
          title="Previous page"
          style={safePage <= 1 ? disabledNavButtonStyle : navButtonStyle}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        {pageItems(safePage, totalPages).map((item, index) => (
          item === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} style={ellipsisStyle}>...</span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              aria-current={item === safePage ? 'page' : undefined}
              style={item === safePage ? activePageButtonStyle : pageButtonStyle}
            >
              {item}
            </button>
          )
        ))}
        <button
          type="button"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= totalPages}
          aria-label="Next page"
          title="Next page"
          style={safePage >= totalPages ? disabledNavButtonStyle : navButtonStyle}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </nav>
    </div>
  )
}

const wrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  marginTop: 14,
  minWidth: 0,
}

const summaryStyle: CSSProperties = {
  alignSelf: 'stretch',
  color: '#666',
  fontSize: 12,
  minWidth: 0,
  overflowWrap: 'anywhere',
}

const controlsStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  maxWidth: '100%',
  minWidth: 0,
}

const pageButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  border: '1px solid #d8d8df',
  borderRadius: 4,
  background: '#fff',
  color: '#333',
  fontSize: 12,
  cursor: 'pointer',
  outlineOffset: 2,
}

const activePageButtonStyle: CSSProperties = {
  ...pageButtonStyle,
  border: '1px solid #3949ab',
  background: '#3949ab',
  color: '#fff',
  fontWeight: 700,
}

const navButtonStyle: CSSProperties = {
  ...pageButtonStyle,
  width: 36,
  padding: 0,
}

const disabledNavButtonStyle: CSSProperties = {
  ...navButtonStyle,
  color: '#aaa',
  background: '#f6f6f6',
  cursor: 'not-allowed',
}

const ellipsisStyle: CSSProperties = {
  minWidth: 20,
  color: '#777',
  fontSize: 12,
  textAlign: 'center',
}
